/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import usePass from '../../../util/hooks/usePass';
import { FormattedMessage } from '../../../util/reactIntl';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
} from '../../../util/types';
import { adjustLineItemsForDisplay } from './adjustLineItemsForDisplay';
import { feeInfoStyles } from './BookingBreakdown.styles';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemKnownItemsMaybe from './LineItemKnownItemsMaybe';
import LineItemKnownRefundMaybe from './LineItemKnownRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundSubTotalMaybe from './LineItemRefundSubTotalMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnitsRefundMaybe from './LineItemUnitsRefundMaybe';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import LineItemUnknownRefundMaybe from './LineItemUnknownRefundMaybe';
import LineItemVoucherMaybe from './LineItemVoucherMaybe';
import LineItemVoucherRefundMaybe from './LineItemVoucherRefundMaybe';

const BookingBreakdown = ({
  userRole,
  unitType,
  transaction,
  booking,
  dateType = null,
  toggleState = true,
  customLineItemTranslations,
  showBookingPeriod = true,
}) => {
  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const [hasValidPass, pass] = usePass();

  // Everything a listing contingent or an annual pass already covers is shown
  // as free. The adjustment is made here so that every row, the subtotal and
  // the total read the same amounts, no matter in which order they render.
  const lineItems = adjustLineItemsForDisplay(transaction, userRole, pass, hasValidPass);

  const hasCommissionLineItem = lineItems.find((item) => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission =
      isProvider &&
      (item.code === LINE_ITEM_PROVIDER_COMMISSION ||
        item.code === LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION);
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const contingentTransaction = transaction.attributes.metadata?.contingentTransaction;
  const accepted = transaction.booking?.attributes?.state === 'accepted';

  /**
   * BookingBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemUnitsMaybe: if he unitType is line-item/unit print the name and
   * quantity of the unit
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnitPriceMaybe: prints just the unit price, e.g. "Price per night $32.00".
   * This line item is not used by default in the BookingBreakdown.
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemRefundSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemUnknownRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by FTW doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <Box>
      <Box display={toggleState ? 'block' : 'none'}>
        {showBookingPeriod ? (
          <LineItemBookingPeriod booking={booking} dateType={dateType} unitType={unitType} />) : null}

        <LineItemBasePriceMaybe
          lineItems={lineItems}
          transaction={transaction}
          userRole={userRole}
        />

        <LineItemKnownItemsMaybe
          isProvider={isProvider}
          lineItems={lineItems}
          transaction={transaction}
        />

        <LineItemUnknownItemsMaybe
          customLineItemTranslations={customLineItemTranslations}
          isProvider={isProvider}
          transaction={transaction}
        />

        <LineItemSubTotalMaybe lineItems={lineItems} userRole={userRole} />

        <LineItemUnitsRefundMaybe transaction={transaction} userRole={userRole} />

        <LineItemKnownRefundMaybe transaction={transaction} userRole={userRole} />

        <LineItemUnknownRefundMaybe
          customLineItemTranslations={customLineItemTranslations}
          transaction={transaction}
          userRole={userRole}
        />

        <LineItemRefundSubTotalMaybe lineItems={lineItems} userRole={userRole} />

        <LineItemCustomerCommissionMaybe isCustomer={isCustomer} lineItems={lineItems} />

        <LineItemCustomerCommissionRefundMaybe
          isCustomer={isCustomer}
          transaction={transaction}
        />

        <LineItemProviderCommissionMaybe isProvider={isProvider} transaction={transaction} />

        <LineItemProviderCommissionRefundMaybe
          isProvider={isProvider}
          transaction={transaction}
        />

        <LineItemVoucherMaybe isCustomer={isCustomer} lineItems={lineItems} />

        <LineItemVoucherRefundMaybe isCustomer={isCustomer} transaction={transaction} />
      </Box>

      <LineItemTotalPrice
        lineItems={lineItems}
        transaction={transaction}
        userRole={userRole}
      />

      {contingentTransaction && isProvider ? (
        <Box sx={feeInfoStyles}>
          <Typography variant="lineItemMuted">
            {accepted ? (
              <FormattedMessage id="BookingBreakdown.contingentTransactionNode" />
            ) : (
              <FormattedMessage id="BookingBreakdown.contingentTransactionNode.notAccepted" />
            )}
          </Typography>
        </Box>
      ) : null}

      {hasCommissionLineItem && toggleState ? (
        <Box sx={feeInfoStyles}>
          <Typography variant="lineItemMuted">
            <FormattedMessage id="BookingBreakdown.commissionFeeNote" />
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default BookingBreakdown;
